import type { EChartsOption } from 'echarts';

/**
 * Read a CSS custom property from the given element (or document root).
 * CSS custom properties defined on `:host` of the dashboard cascade to
 * all child chart presenters via CSS inheritance, so any descendant
 * element will resolve the `--pr-chart-colour-*` tokens.
 */
export function getCssVar(
  name: string,
  element?: Element,
  fallback: string = ''
): string {
  if (typeof document === 'undefined') {
    return fallback;
  }
  const target = element ?? document.documentElement;
  return getComputedStyle(target).getPropertyValue(name).trim() || fallback;
}

/**
 * 6-colour categorical palette for chart series.
 * Each entry reads an `--pr-chart-colour-*` CSS custom property defined on the
 * dashboard host element (see ai-receptionist-dashboard.component.scss).
 *
 * The properties are derived from Tailwind tokens so the palette
 * follows the design system automatically.
 */
function buildPalette(element?: Element): string[] {
  return [
    getCssVar('--pr-chart-colour-primary', element),
    getCssVar('--pr-chart-colour-secondary', element),
    getCssVar('--pr-chart-colour-warn', element),
    getCssVar('--pr-chart-colour-amber', element),
    getCssVar('--pr-chart-colour-teal', element),
    getCssVar('--pr-chart-colour-success', element),
    getCssVar('--pr-chart-colour-accent', element),
  ];
}

/**
 * Build the Ovie light theme from Tailwind CSS custom properties.
 *
 * Call this **after** the dashboard component has rendered so the
 * `--pr-chart-colour-*` custom properties are available on `:host`.
 *
 * Pass the returned object as `[theme]` on individual `<div echarts>`
 * directives — it is NOT registered globally via `echarts.registerTheme`.
 */
export function buildEchartsTheme(element?: Element): EChartsOption {
  const textColour = getCssVar('--pr-chart-colour-text', element);
  const textSecondary = getCssVar('--pr-chart-colour-text-secondary', element);
  const textMuted = getCssVar('--pr-chart-colour-text-muted', element);
  const borderColour = getCssVar('--pr-chart-colour-border', element);
  const backgroundColour = getCssVar('--pr-chart-colour-background', element);
  const palette = buildPalette(element);

  return {
    color: palette,
    backgroundColor: 'transparent',
    textStyle: {
      color: textColour,
      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
    },
    title: {
      textStyle: { color: textColour, fontSize: 14, fontWeight: 600 },
      subtextStyle: { color: textSecondary, fontSize: 12 },
    },
    legend: {
      textStyle: { color: textSecondary, fontSize: 12 },
    },
    tooltip: {
      backgroundColor: backgroundColour,
      borderColor: borderColour,
      textStyle: { color: textColour, fontSize: 12 },
    },
    grid: {
      left: 48,
      right: 16,
      top: 48,
      bottom: 32,
      containLabel: true,
    },
    xAxis: {
      axisLine: { lineStyle: { color: borderColour } },
      axisTick: { lineStyle: { color: borderColour } },
      axisLabel: { color: textMuted, fontSize: 11 },
      splitLine: { lineStyle: { color: borderColour, type: 'dashed' } },
    },
    yAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: textMuted, fontSize: 11 },
      splitLine: { lineStyle: { color: borderColour, type: 'dashed' } },
    },
  };
}

/** Placeholder for future dark-mode theme. */
export const ECHARTS_DARK_THEME: EChartsOption = {};
